import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  DatePicker,
  Select,
  Upload,
  message,
  Modal,
  Table,
  Space,
  Tag,
  Card,
  Divider,
  Typography,
  Empty,
  Badge,
  Alert,
  Spin,
  Tooltip,
} from "antd";
import {
  InboxOutlined,
  DeleteOutlined,
  PlusOutlined,
  MailOutlined,
  UserOutlined,
  InfoCircleOutlined,
  CodeOutlined,
  EditOutlined,
  PlusCircleOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  StarFilled,
  LinkOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import CampaignTemplateGallery from "@views/superAdmin/emails/emailCampaigns/components/CampaignTemplateGallery";
import { withRouter } from "react-router";
import * as XLSX from "xlsx";
import {
  createEmailCampaign,
  getEmailCampaignMetadata,
} from "@api/email/emailCampaign";
import { getHtmlTemplateById } from "@api/email/htmlTemplate";
import { getActiveUserEmails } from "@api/email/users/userEmail";
import { searchUsers, getUserCountByDateRange } from "@api/user/superAdmin";
import {
  SuccessNotification,
  ErrorNotification,
  EmailVolumeControl,
} from "@components";
import Papa from "papaparse";
import moment from "moment";

const { Dragger } = Upload;
const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

// Updated initial state for email campaign
const initialState = {
  campaignType: "all",
  name: "",
  fromName: "",
  subject: "",
  htmlTemplate: "",
  scheduledFor: null,
  status: "draft",
  includedPlans: [],
  targetedUsers: [],
  externalRecipients: [],
  dateRangeUsers: [],
  emailsToUse: [],
  replyTo: null,
  cc: [],
  bcc: [],
  variables: [],
  emailSendingVolume: 1,
  dateRangeFilter: {
    startDate: null,
    endDate: null,
  },
};

const EmailCampaignCreateForm = ({ history }) => {
  const [values, setValues] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [plans, setPlans] = useState([]);
  const [excludedUsers, setExcludedUsers] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [fileRecipients, setFileRecipients] = useState({});
  const [availableEmails, setAvailableEmails] = useState([]);
  const [ccInput, setCcInput] = useState("");
  const [bccInput, setBccInput] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [editingVariable, setEditingVariable] = useState(null);
  const [variableModalVisible, setVariableModalVisible] = useState(false);
  const [availableColumns, setAvailableColumns] = useState([
    { key: "email", title: "Email", required: true },
    { key: "name", title: "Name", required: false },
  ]);
  const [newRecipientData, setNewRecipientData] = useState({
    email: "",
    name: "",
  });
  const [recipientColumns, setRecipientColumns] = useState(
    new Set(["email", "name"])
  );
  // Date range filter
  const [dateRangeUsers, setDateRangeUsers] = useState([]);
  const [fetchingUserCount, setFetchingUserCount] = useState(false);

  // Template gallery
  const [templateGalleryVisible, setTemplateGalleryVisible] = useState(false);

  // HTML Template preview
  const [templatePreviewVisible, setTemplatePreviewVisible] = useState(false);
  const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false);
  const [templatePreviewData, setTemplatePreviewData] = useState(null);

  // Auto-selected HTML template tracking
  const [autoSelectedHtmlTemplate, setAutoSelectedHtmlTemplate] = useState(null);
  const [htmlTemplateMatchInfo, setHtmlTemplateMatchInfo] = useState(null);

  const [timezone, setTimezone] = useState(moment.tz.guess());
  const timezones = moment.tz.names();

  // Function to format timezone with GMT offset
  const formatTimezone = (tz) => {
    const offset = moment.tz(tz).utcOffset();
    const hours = Math.floor(Math.abs(offset) / 60);
    const minutes = Math.abs(offset) % 60;
    const sign = offset >= 0 ? "+" : "-";
    const formattedOffset = `GMT${sign}${hours
      .toString()
      .padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
    return `${tz} (${formattedOffset})`;
  };

  const handleTimezoneChange = (value) => {
    setTimezone(value);
  };

  const handleDateChange = (date) => {
    if (date) {
      const formattedDate = date.tz(timezone).format();
      setValues({ ...values, scheduledFor: formattedDate });
    }
  };

  // Fetch email campaign metadata on component mount
  useEffect(() => {
    fetchEmailCampaignMetadata();
    fetchAvailableEmails();
  }, []);

  // Add this function to fetch available emails
  const fetchAvailableEmails = async () => {
    try {
      const response = await getActiveUserEmails();
      // Group emails by domain
      const emailsByDomain = response.data.reduce((acc, email) => {
        const domain = email.email.split("@")[1];
        if (!acc[domain]) {
          acc[domain] = [];
        }
        acc[domain].push(email);
        return acc;
      }, {});
      setAvailableEmails(emailsByDomain);
    } catch (error) {
      console.error("Error fetching available emails:", error);
      ErrorNotification("Failed to fetch available emails");
    }
  };

  // Function to fetch email campaign metadata
  const fetchEmailCampaignMetadata = async () => {
    try {
      const response = await getEmailCampaignMetadata();
      setPlans(response.data.plans || []); // Default to an empty array if no plans are available
      setTemplates(response.data.htmlTemplates || []); // Default to an empty array for templates
    } catch (error) {
      console.error("Error fetching email campaign metadata:", error);
      ErrorNotification("Failed to fetch email campaign metadata");
      setPlans([]); // Fallback to an empty array in case of error
    }
  };

  // Function to handle date range change
  const handleDateRangeChange = async (dates) => {
    if (!dates || dates.length !== 2) {
      setValues({
        ...values,
        dateRangeFilter: {
          startDate: null,
          endDate: null,
        },
      });
      setDateRangeUsers([]);
      return;
    }

    const [startDate, endDate] = dates;

    setValues({
      ...values,
      dateRangeFilter: {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      },
    });

    // Fetch users for selected date range
    setFetchingUserCount(true);
    try {
      const response = await getUserCountByDateRange({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });
      setDateRangeUsers(response.data.users);
    } catch (error) {
      console.error("Error fetching users:", error);
      ErrorNotification("Failed to fetch users for date range");
      setDateRangeUsers([]);
    } finally {
      setFetchingUserCount(false);
    }
  };

  const dateRangeColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Join Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (date) => moment(date).format("MMMM Do YYYY, h:mm:ss a"),
    },
  ];

  // Function to format email display
  const formatEmailDisplay = (email) => {
    return `${email.name || email.email.split("@")[0]} <${email.email}>`;
  };

  // Handle form changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setValues({
      ...values,
      [name]: value,
    });
  };

  const formatEmailsForSelect = () => {
    return Object.entries(availableEmails).map(([domain, emails]) => {
      const options = emails
        .map((email) => {
          // Base email option
          const baseOption = {
            value: `email:${email._id}`,
            label: (
              <Space>
                <MailOutlined />
                <span>{email.name || email.email.split("@")[0]}</span>
                <Text type="secondary">&lt;{email.email}&gt;</Text>
              </Space>
            ),
            email: email,
          };

          // Alias options for this email
          const aliasOptions = (email.aliases || []).map((alias) => ({
            value: `alias:${email._id}:${alias}`,
            label: (
              <Space style={{ paddingLeft: 24 }}>
                <UserOutlined />
                <span>{alias.split("@")[0]}</span>
                <Text type="secondary">&lt;{alias}&gt;</Text>
                <Badge
                  count="Alias"
                  style={{
                    backgroundColor: "#e6f4ff",
                    color: "#1890ff",
                    fontSize: "12px",
                    border: "1px solid #91caff",
                  }}
                />
              </Space>
            ),
            parentEmail: email,
            isAlias: true,
          }));

          return [baseOption, ...aliasOptions];
        })
        .flat();

      return {
        label: (
          <Space>
            <span>Domain: {domain}</span>
            <Text type="secondary">
              ({emails.length} email{emails.length !== 1 ? "s" : ""})
            </Text>
          </Space>
        ),
        options,
      };
    });
  };

  const handleEmailSelection = (selectedValues) => {
    const formattedEmails = selectedValues.map((value) => {
      const [type, emailId, alias] = value.split(":");

      return {
        email: emailId,
        useAlias: type === "alias",
        selectedAlias: type === "alias" ? alias : null,
      };
    });

    setSelectedEmails(selectedValues);
    setValues({ ...values, emailsToUse: formattedEmails });
  };

  const getSelectedEmailDetails = () => {
    const details = {
      totalEmails: selectedEmails.length,
      baseEmails: 0,
      aliases: 0,
      domains: new Set(),
    };

    selectedEmails.forEach((value) => {
      const [type, emailId, alias] = value.split(":");
      if (type === "email") {
        details.baseEmails++;
        const email = Object.values(availableEmails)
          .flat()
          .find((e) => e._id === emailId);
        if (email) {
          details.domains.add(email.email.split("@")[1]);
        }
      } else {
        details.aliases++;
        details.domains.add(alias.split("@")[1]);
      }
    });

    return details;
  };

  const selectedDetails = getSelectedEmailDetails();

  // Handle campaign type change
  const handleCampaignTypeChange = (value) => {
    setValues({
      ...values,
      campaignType: value,
      includedPlans: [],
      targetedUsers: [],
      externalRecipients: [],
    });
    setExcludedUsers([]);
    setDateRangeUsers(null);
  };

  /**
   * Smart HTML template matching for campaign templates
   * Matches based on: 1) Exact templateId, 2) Category, 3) Purpose, 4) Any available
   *
   * Cold outreach campaign templates have suggestedMjmlTemplateId like:
   * - coldOutreach-problemAware
   * - coldOutreach-valueAdd
   * - coldOutreach-socialProof
   * - coldOutreach-breakup
   * - coldOutreach-meetingRequest
   */
  const findMatchingHtmlTemplate = (campaignTemplate) => {
    if (!templates || templates.length === 0) {
      return { template: null, matchType: null, matchReason: null };
    }

    // Get suggested MJML template ID from first email
    const suggestedId = campaignTemplate.emails?.[0]?.suggestedMjmlTemplateId;

    // 1. Try exact match by templateId (highest priority)
    if (suggestedId) {
      const exactMatch = templates.find((t) => t.templateId === suggestedId);
      if (exactMatch) {
        return {
          template: exactMatch,
          matchType: "exact",
          matchReason: `Perfect match - designed for "${campaignTemplate.name}"`,
        };
      }

      // 1b. Try partial match on templateId prefix (e.g., "coldOutreach" part)
      const idPrefix = suggestedId.split("-")[0];
      const prefixMatch = templates.find((t) => t.templateId?.startsWith(idPrefix));
      if (prefixMatch) {
        return {
          template: prefixMatch,
          matchType: "partial",
          matchReason: `Best match for ${idPrefix} campaigns`,
        };
      }
    }

    // 2. Map campaign category to HTML template category (prioritize coldOutreach for prospecting)
    const categoryMapping = {
      prospecting: ["coldOutreach", "marketing"],
      followup: ["coldOutreach", "sequences", "marketing"],
      meeting: ["coldOutreach", "events", "marketing"],
      event: ["events", "marketing"],
      abm: ["coldOutreach", "marketing"],
      reengagement: ["sequences", "coldOutreach", "marketing"],
      industry: ["coldOutreach", "marketing"],
      partner: ["coldOutreach", "marketing"],
    };

    const mappedCategories = categoryMapping[campaignTemplate.category] || ["coldOutreach", "marketing"];

    for (const category of mappedCategories) {
      const categoryMatch = templates.find((t) => t.category === category);
      if (categoryMatch) {
        return {
          template: categoryMatch,
          matchType: "category",
          matchReason: `Recommended for ${campaignTemplate.category} campaigns`,
        };
      }
    }

    // 3. Try purpose-based matching
    const purposeMapping = {
      prospecting: "campaign",
      followup: "sequence",
      meeting: "campaign",
      event: "campaign",
      abm: "campaign",
      reengagement: "sequence",
      industry: "campaign",
      partner: "campaign",
    };

    const targetPurpose = purposeMapping[campaignTemplate.category] || "campaign";
    const purposeMatch = templates.find((t) => t.purpose === targetPurpose);
    if (purposeMatch) {
      return {
        template: purposeMatch,
        matchType: "purpose",
        matchReason: `Suitable for ${targetPurpose} emails`,
      };
    }

    // 4. Fallback to first default template or any template
    const defaultTemplate = templates.find((t) => t.isDefault) || templates[0];
    return {
      template: defaultTemplate,
      matchType: "fallback",
      matchReason: "Default template selected - feel free to change",
    };
  };

  // Handle template selection from gallery
  const handleTemplateSelect = (template) => {
    // Generate campaign name from template
    const campaignName = `${template.name} - ${new Date().toLocaleDateString()}`;

    // Extract subject from first email in template
    const firstEmailSubject = template.emails[0]?.subject || "";

    // Convert template variables to the format expected by the form
    const templateVariables = template.variables?.map((v) => ({
      key: v.key,
      defaultValue: v.defaultValue,
      description: v.description || "",
    })) || [];

    // Find matching HTML template
    const { template: matchedHtmlTemplate, matchType, matchReason } = findMatchingHtmlTemplate(template);

    // Update state with matched template info
    if (matchedHtmlTemplate) {
      setAutoSelectedHtmlTemplate(matchedHtmlTemplate);
      setHtmlTemplateMatchInfo({ matchType, matchReason, campaignTemplate: template.name });
    }

    // Pre-fill form values
    setValues({
      ...values,
      campaignType: "external", // Cold outreach campaigns are typically external
      name: campaignName,
      subject: firstEmailSubject,
      variables: templateVariables,
      htmlTemplate: matchedHtmlTemplate?._id || "",
      // Store template metadata for reference
      templateId: template.id,
      templateCategory: template.category,
    });

    // Show success message with template info
    const templateMsg = matchedHtmlTemplate
      ? ` HTML template "${matchedHtmlTemplate.name}" auto-selected.`
      : "";
    message.success(`Template "${template.name}" loaded.${templateMsg} Customize as needed.`);
    setTemplateGalleryVisible(false);
  };

  // Handle manual HTML template change
  const handleHtmlTemplateChange = (value) => {
    setValues({ ...values, htmlTemplate: value });
    // Clear auto-selection info when user manually changes
    if (autoSelectedHtmlTemplate && value !== autoSelectedHtmlTemplate._id) {
      setAutoSelectedHtmlTemplate(null);
      setHtmlTemplateMatchInfo(null);
    }
  };

  // Handle HTML template preview
  const handleTemplatePreview = async () => {
    if (!values.htmlTemplate) {
      message.warning("Please select an HTML template first");
      return;
    }

    setTemplatePreviewLoading(true);
    setTemplatePreviewVisible(true);

    try {
      const res = await getHtmlTemplateById(values.htmlTemplate);
      setTemplatePreviewData(res.data);
    } catch (error) {
      console.error("Error fetching template for preview", error);
      message.error("Failed to load template preview");
      setTemplatePreviewVisible(false);
    } finally {
      setTemplatePreviewLoading(false);
    }
  };

  // Handle user search for exclusion or targeting
  const handleUserSearch = async (value) => {
    if (value.length > 2) {
      setSearching(true);
      try {
        const response = await searchUsers(value);
        const options = response.data.map((user) => ({
          value: user._id,
          label: `${user.email} (${user.name})`,
          email: user.email,
        }));
        setUserOptions(options);
      } catch (error) {
        console.error("Error searching users:", error);
      } finally {
        setSearching(false);
      }
    } else {
      setUserOptions([]);
    }
  };

  // Handle changes in excluded users
  const handleExcludedUsersChange = (selectedUserIds) => {
    setExcludedUsers(selectedUserIds);
  };

  // Handle changes in targeted users
  const handleTargetedUsersChange = (selectedUserIds) => {
    setValues({ ...values, targetedUsers: selectedUserIds });
  };

  // Enhanced function to parse emails from various formats
  const parseCSVData = (data) => {
    if (!data || data.length === 0) return [];

    // Get headers from first row, creating a mapping between display name and key
    const headers = data[0].map((header) => {
      const displayName = header.trim(); // Keep original for display
      const key = header.toLowerCase().trim(); // Simple lowercase for key
      return {
        key,
        title: displayName,
        required: key === "email",
      };
    });

    // Update available columns with the new mapping
    const newColumns = new Set([...recipientColumns]);
    headers.forEach((header) => newColumns.add(header.key));
    setRecipientColumns(newColumns);
    setAvailableColumns((prevColumns) => {
      const existingKeys = new Set(prevColumns.map((col) => col.key));
      const newHeaders = headers.filter(
        (header) => !existingKeys.has(header.key)
      );
      return [...prevColumns, ...newHeaders];
    });

    // Parse rows into objects
    const recipients = [];
    for (let i = 1; i < data.length; i++) {
      const row = data[i];
      const recipient = {};

      headers.forEach((header, index) => {
        const value = row[index]?.toString().trim() || "";
        if (value) {
          recipient[header.key] = value;
        }
      });

      // Only add if email is present and valid
      if (recipient.email && isValidEmail(recipient.email)) {
        recipients.push(recipient);
      }
    }

    return recipients;
  };

  const processFile = (file) => {
    return new Promise((resolve, reject) => {
      const fileObj = file.originFileObj || file;

      if (fileObj instanceof Blob) {
        const reader = new FileReader();

        reader.onload = async (e) => {
          const content = e.target.result;
          let parsedData;

          try {
            if (file.name.endsWith(".csv")) {
              parsedData = Papa.parse(content, {
                skipEmptyLines: true,
                header: false,
              }).data;
              const recipients = parseCSVData(parsedData);
              handleParsedRecipients(recipients, file.name);
            } else if (file.name.match(/\.(xlsx|xls)$/i)) {
              // For Excel files, we need to use ArrayBuffer
              const workbook = XLSX.read(content, { type: "array" });
              const firstSheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[firstSheetName];
              parsedData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
              const recipients = parseCSVData(parsedData); // We can reuse the same parsing logic
              handleParsedRecipients(recipients, file.name);
            } else if (file.name.endsWith(".txt")) {
              // For text files, split by newline and parse
              const lines = content
                .split(/\r?\n/)
                .filter((line) => line.trim());
              let recipients = [];

              // Check if the first line might be headers
              const firstLine = lines[0].trim();
              const possibleHeaders = firstLine.split(/[,\t]/);

              if (
                possibleHeaders.length > 1 &&
                possibleHeaders.some((h) => h.toLowerCase().includes("email"))
              ) {
                // Treat as CSV/TSV format with headers
                parsedData = lines.map((line) => line.split(/[,\t]/));
                recipients = parseCSVData(parsedData);
              } else {
                // Treat as simple email list
                recipients = lines
                  .filter((line) => isValidEmail(line.trim()))
                  .map((line) => ({
                    email: line.trim(),
                    name: "",
                  }));
              }
              handleParsedRecipients(recipients, file.name);
            }

            resolve();
          } catch (error) {
            console.error("Error processing file:", error);
            message.error(
              `Failed to process ${file.name}. Please check the file format.`
            );
            reject(error);
          }
        };

        reader.onerror = (error) => {
          console.error("Error reading file:", error);
          message.error(
            `Failed to read the file ${file.name}. Please try again.`
          );
          reject(error);
        };

        // Read the file based on its type
        if (file.name.match(/\.(xlsx|xls)$/i)) {
          reader.readAsArrayBuffer(fileObj);
        } else {
          reader.readAsText(fileObj);
        }
      } else {
        console.error("Invalid file object:", fileObj);
        message.error(`Invalid file ${file.name}. Please try uploading again.`);
        reject(new Error("Invalid file object"));
      }
    });
  };

  // Helper function to handle parsed recipients
  const handleParsedRecipients = (recipients, fileName) => {
    // Format recipients according to the schema structure
    const formattedRecipients = recipients.map((recipient) => {
      // Extract email field
      const { email, ...otherFields } = recipient;

      // Create values Map with remaining fields
      const values = new Map();
      Object.entries(otherFields).forEach(([key, value]) => {
        if (value) {
          // Only add non-empty values
          values.set(key, value);
        }
      });

      return {
        email: email.toLowerCase(),
        values: Object.fromEntries(values), // Convert Map to object for API
      };
    });

    setFileRecipients((prev) => ({
      ...prev,
      [fileName]: formattedRecipients,
    }));

    setValues((prevValues) => {
      const allRecipients = [
        ...Object.values({
          ...fileRecipients,
          [fileName]: formattedRecipients,
        }).flat(),
      ];

      // Deduplicate based on email
      const uniqueRecipients = allRecipients.reduce((acc, current) => {
        const existing = acc.find((item) => item.email === current.email);
        if (!existing) {
          acc.push(current);
        }
        return acc;
      }, []);

      return { ...prevValues, externalRecipients: uniqueRecipients };
    });

    // Initialize newRecipientData with all available columns
    setNewRecipientData((prevData) => {
      const newData = { ...prevData };
      Array.from(recipientColumns).forEach((column) => {
        if (!newData.hasOwnProperty(column)) {
          newData[column] = "";
        }
      });
      return newData;
    });

    message.success(`${recipients.length} recipients loaded from ${fileName}`);
  };

  // Handle file upload
  const handleFileUpload = async (info) => {
    const { file, fileList } = info;

    if (file.status === "removed") {
      // Remove recipients associated with the deleted file
      const updatedFileRecipients = { ...fileRecipients };
      delete updatedFileRecipients[file.name];
      setFileRecipients(updatedFileRecipients);

      // Recalculate externalRecipients
      const allRecipients = Object.values(updatedFileRecipients).flat();
      setValues({ ...values, externalRecipients: allRecipients });

      message.info(`File ${file.name} removed. Recipient list updated.`);
      return;
    }

    // Process new files
    for (let i = 0; i < fileList.length; i++) {
      const currentFile = fileList[i];
      if (currentFile.status !== "done") {
        await processFile(currentFile);
      }
    }
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleModalOk = () => {
    setIsModalVisible(false);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  const handleAddRecipient = () => {
    if (newRecipientData.email && isValidEmail(newRecipientData.email)) {
      const { email, ...otherFields } = newRecipientData;

      // Format the recipient according to schema
      const newRecipient = {
        email: email.toLowerCase(),
        values: {}, // Initialize empty values object
      };

      // Add all other fields to the values object
      Object.keys(otherFields).forEach((key) => {
        if (otherFields[key]) {
          // Only add non-empty values
          newRecipient.values[key] = otherFields[key];
        }
      });

      setValues((prevValues) => ({
        ...prevValues,
        externalRecipients: Array.isArray(prevValues.externalRecipients)
          ? [...prevValues.externalRecipients, newRecipient]
          : [newRecipient],
      }));

      // Reset form but maintain structure
      setNewRecipientData((prevData) => {
        const resetData = { ...prevData };
        Object.keys(resetData).forEach((key) => {
          resetData[key] = "";
        });
        return resetData;
      });
    } else {
      message.error("Please enter a valid email address");
    }
  };

  const handleRemoveRecipient = (email) => {
    setValues({
      ...values,
      externalRecipients: values.externalRecipients.filter(
        (r) => r.email !== email
      ),
    });
  };

  const getTableColumns = () => {
    const columns = Array.from(recipientColumns).map((key) => ({
      title: availableColumns.find((col) => col.key === key)?.title || key,
      dataIndex: ["values", key],
      key: key,
      ellipsis: true,
      render: (text, record) => {
        if (key === "email") {
          return record.email;
        }
        return record.values?.[key] || "";
      },
    }));

    return [
      ...columns,
      {
        title: "Action",
        key: "action",
        width: 80,
        render: (_, record) => (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleRemoveRecipient(record.email)}
          />
        ),
      },
    ];
  };

  const renderRecipientModal = () => (
    <Modal
      title="Recipient List"
      visible={isModalVisible}
      onOk={handleModalOk}
      onCancel={handleModalCancel}
      width={1200}
      style={{ top: 20 }}
      footer={[
        <Button key="back" onClick={handleModalCancel}>
          Close
        </Button>,
      ]}
    >
      <div style={{ marginBottom: 24 }}>
        <Card title="Add New Recipient" size="small">
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              flexWrap: "wrap",
              gap: "16px",
              marginBottom: "16px",
            }}
          >
            {Array.from(recipientColumns).map((column) => {
              const columnDef = availableColumns.find(
                (col) => col.key === column
              );
              return (
                <Form.Item
                  key={column}
                  label={columnDef?.title || column}
                  style={{ margin: 0, minWidth: 200, flex: "1 1 200px", maxWidth: 300 }}
                  required={columnDef?.required}
                >
                  <Input
                    value={newRecipientData[column]}
                    onChange={(e) =>
                      setNewRecipientData((prev) => ({
                        ...prev,
                        [column]: e.target.value,
                      }))
                    }
                    placeholder={`Enter ${columnDef?.title || column}`}
                    status={
                      column === "email" &&
                      newRecipientData.email &&
                      !isValidEmail(newRecipientData.email)
                        ? "error"
                        : ""
                    }
                  />
                </Form.Item>
              );
            })}
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddRecipient}
              style={{ marginBottom: 0 }}
            >
              Add Recipient
            </Button>
          </div>
        </Card>
      </div>

      <Table
        columns={getTableColumns()}
        dataSource={values.externalRecipients}
        rowKey="email"
        scroll={{ x: "max-content", y: 400 }}
        pagination={{ pageSize: 10 }}
        size="small"
      />
    </Modal>
  );

  const renderExternalRecipientsSection = () => (
    <Form.Item label="External Recipients">
      <Card>
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          {/* Manual Entry Section */}
          <Card size="small" title="Add Recipients Manually">
            <Space direction="vertical" style={{ width: "100%" }}>
              <Input.TextArea
                placeholder="Enter email addresses (one per line or separated by commas)"
                rows={4}
                onBlur={(e) => {
                  const text = e.target.value;
                  const emails = text
                    .split(/[\n,]/)
                    .map((email) => email.trim())
                    .filter((email) => email && isValidEmail(email));

                  if (emails.length > 0) {
                    const newRecipients = emails.map((email) => ({
                      email: email.toLowerCase(),
                      values: {},
                    }));

                    setValues((prev) => ({
                      ...prev,
                      externalRecipients: [
                        ...prev.externalRecipients,
                        ...newRecipients.filter(
                          (newRec) =>
                            !prev.externalRecipients.some(
                              (existingRec) =>
                                existingRec.email === newRec.email
                            )
                        ),
                      ],
                    }));

                    // Clear the textarea
                    e.target.value = "";

                    message.success(`${emails.length} recipient(s) added`);
                  }
                }}
              />
              <Text type="secondary">
                Tip: You can paste multiple email addresses separated by commas
                or new lines
              </Text>
            </Space>
          </Card>

          {/* File Upload Section */}
          <Card size="small" title="Import Recipients from File">
            <Space direction="vertical" style={{ width: "100%" }}>
              <Alert
                message="Import Recipients"
                description={
                  <ul style={{ margin: "0", paddingLeft: "20px" }}>
                    <li>
                      Upload CSV, Excel (.xlsx, .xls), or text files to import
                      recipients
                    </li>
                    <li>All columns from the files will be preserved</li>
                    <li>
                      The email column is required and must contain valid email
                      addresses
                    </li>
                    <li>
                      Additional columns can be used as variables in your email
                      template
                    </li>
                  </ul>
                }
                type="warning"
                showIcon
              />

              <Dragger
                name="file"
                multiple={true}
                beforeUpload={() => false}
                onChange={handleFileUpload}
                accept=".csv,.txt,.xlsx,.xls"
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag files to this area to upload
                </p>
                <p className="ant-upload-hint">
                  Support for CSV, Excel, or text files with recipients
                </p>
              </Dragger>
            </Space>
          </Card>

          {values.externalRecipients.length > 0 && (
            <div style={{ marginTop: "16px" }}>
              <Space>
                <Badge count={values.externalRecipients.length} />
                <Text>recipients loaded</Text>
                <Button
                  onClick={() => {
                    setValues({ ...values, externalRecipients: [] });
                    setFileRecipients({});
                  }}
                >
                  Clear All
                </Button>
                <Button type="primary" onClick={showModal}>
                  View & Edit Recipients
                </Button>
              </Space>
            </div>
          )}
        </Space>
      </Card>
    </Form.Item>
  );

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle CC emails
  const handleCcInputChange = (e) => {
    setCcInput(e.target.value);
  };

  const handleCcInputPressEnter = () => {
    const emails = ccInput.split(/[,\s]+/).filter((email) => email.trim());
    const validEmails = emails.filter((email) => isValidEmail(email.trim()));

    if (validEmails.length > 0) {
      setValues({
        ...values,
        cc: [...new Set([...values.cc, ...validEmails])],
      });
      setCcInput("");
    }
  };

  const handleRemoveCc = (emailToRemove) => {
    setValues({
      ...values,
      cc: values.cc.filter((email) => email !== emailToRemove),
    });
  };

  // Handle BCC emails
  const handleBccInputChange = (e) => {
    setBccInput(e.target.value);
  };

  const handleBccInputPressEnter = () => {
    const emails = bccInput.split(/[,\s]+/).filter((email) => email.trim());
    const validEmails = emails.filter((email) => isValidEmail(email.trim()));

    if (validEmails.length > 0) {
      setValues({
        ...values,
        bcc: [...new Set([...values.bcc, ...validEmails])],
      });
      setBccInput("");
    }
  };

  const handleRemoveBcc = (emailToRemove) => {
    setValues({
      ...values,
      bcc: values.bcc.filter((email) => email !== emailToRemove),
    });
  };

  // Handle form submit
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Validate the form
    if (
      !values.name ||
      !values.subject ||
      !values.htmlTemplate ||
      !values.campaignType ||
      !values.emailsToUse?.length ||
      !values.fromName
    ) {
      return ErrorNotification("Please fill all the required fields");
    }

    if (
      values.campaignType === "external" &&
      values.externalRecipients.length === 0
    ) {
      return ErrorNotification("Please upload external recipients");
    }

    // Add date range validation
    if (values.campaignType === "dateRange") {
      if (
        !values.dateRangeFilter?.startDate ||
        !values.dateRangeFilter?.endDate
      ) {
        return ErrorNotification("Please select a date range for the campaign");
      }
      if (!dateRangeUsers.length) {
        return ErrorNotification("No users found in the selected date range");
      }
    }

    // Calculate totalEmails dynamically
    const totalEmails = (() => {
      switch (values.campaignType) {
        case "external":
          return values.externalRecipients.length;
        case "dateRange":
          return dateRangeUsers.length;
        case "targeted":
          return values.targetedUsers.length;
        case "planBased":
          // For plan based campaigns, we don't need to validate totalEmails here
          // as it will be calculated on the server side based on the selected plans
          return 1; // Return a non-zero value to pass validation
        case "all":
          // Assuming total users for 'all' is fetched from metadata or API
          return values.totalUsers || 0;
        default:
          return 0;
      }
    })();

    // Check if totalEmails is valid
    if (totalEmails === 0) {
      return ErrorNotification("No recipients found for this campaign.");
    }

    // Prepare campaign data
    let campaignData = {
      ...values,
      totalEmails, // Include the calculated totalEmails
      dateRangeUsers:
        values.campaignType === "dateRange"
          ? dateRangeUsers.map((user) => ({
              user: user._id,
              metadata: {
                email: user.email,
                name: user.name,
              },
            }))
          : [],
      excludedUsers:
        values.campaignType !== "targeted"
          ? excludedUsers.map((user) => user.value)
          : [],
    };

    setLoading(true);
    try {
      await createEmailCampaign(campaignData);
      setLoading(false);
      SuccessNotification(`Email campaign created successfully`);
      setValues(initialState);
      setExcludedUsers([]);
      history.goBack();
    } catch (err) {
      setLoading(false);
      ErrorNotification(
        err.response?.data?.message || "Error creating email campaign"
      );
      console.error("CREATE_EMAIL_CAMPAIGN_ERROR", err.response?.data?.message);
    }
  };

  const VariableForm = ({ variable, onSave, onCancel }) => {
    const [form] = Form.useForm();

    useEffect(() => {
      form.setFieldsValue(variable || {});
    }, [variable, form]);

    const handleSubmit = () => {
      form.validateFields().then((values) => {
        onSave(values);
        form.resetFields();
      });
    };

    return (
      <Modal
        title={variable ? "Edit Variable" : "Add Variable"}
        visible={variableModalVisible}
        onOk={handleSubmit}
        onCancel={onCancel}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="key"
            label="Variable Key"
            rules={[
              { required: true, message: "Please enter a variable key" },
              {
                pattern: /^[a-zA-Z][a-zA-Z0-9_]*$/,
                message:
                  "Key must start with a letter and contain only letters, numbers, and underscores",
              },
            ]}
            tooltip="This will be used in the template as {{variable_key}}"
          >
            <Input
              prefix={<CodeOutlined />}
              placeholder="e.g., company_name"
              disabled={!!variable}
            />
          </Form.Item>
          <Form.Item
            name="defaultValue"
            label="Default Value"
            rules={[
              { required: true, message: "Please enter a default value" },
            ]}
            tooltip="This value will be used if no specific value is provided"
          >
            <Input placeholder="Enter default value" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            tooltip="Helpful description of what this variable is used for"
          >
            <Input.TextArea
              placeholder="Describe what this variable represents..."
              rows={3}
            />
          </Form.Item>
        </Form>
      </Modal>
    );
  };

  // Add these handlers for variable management
  const handleAddVariable = () => {
    setEditingVariable(null);
    setVariableModalVisible(true);
  };

  const handleEditVariable = (variable) => {
    setEditingVariable(variable);
    setVariableModalVisible(true);
  };

  const handleSaveVariable = (variableData) => {
    setValues((prev) => {
      const newVariables = [...prev.variables];
      if (editingVariable) {
        // Edit existing variable
        const index = newVariables.findIndex(
          (v) => v.key === editingVariable.key
        );
        if (index !== -1) {
          newVariables[index] = variableData;
        }
      } else {
        // Add new variable
        newVariables.push(variableData);
      }
      return { ...prev, variables: newVariables };
    });
    setVariableModalVisible(false);
  };

  const renderReplyToSection = () => {
    // Helper function to get proper value for the Select
    const getReplyToValue = () => {
      if (!values.replyTo) return undefined;

      // If it's an ID (from original email selection)
      const emailFromId = Object.values(availableEmails)
        .flat()
        .find((e) => e._id === values.replyTo);

      if (emailFromId) {
        return emailFromId.email;
      }

      // If it's already an email address (from manual entry)
      return values.replyTo;
    };

    return (
      <Form.Item
        label="Reply-To"
        tooltip={{
          title: "Email address that will receive replies to this campaign",
          icon: <InfoCircleOutlined />,
        }}
      >
        <Select
          style={{ width: "100%" }}
          placeholder="Select or enter reply-to email address"
          onChange={(value) => {
            // Handle clearing the value
            if (!value || value.length === 0) {
              setValues({ ...values, replyTo: undefined });
              return;
            }

            // Get the last value if it's an array (from tags mode)
            const actualValue = Array.isArray(value)
              ? value[value.length - 1]
              : value;

            // If the value is a valid email, use it directly
            if (isValidEmail(actualValue)) {
              setValues({ ...values, replyTo: actualValue });
              return;
            }

            // Otherwise, try to find the email object and use its ID
            const emailObj = Object.values(availableEmails)
              .flat()
              .find((e) => e.email === actualValue);

            if (emailObj) {
              setValues({ ...values, replyTo: emailObj._id });
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              const value = e.target.value;
              if (isValidEmail(value)) {
                setValues({ ...values, replyTo: value });
                setDropdownOpen(false);
              }
            }
          }}
          value={getReplyToValue()}
          optionFilterProp="label"
          optionLabelProp="label"
          allowClear
          showSearch
          showArrow
          mode="tags"
          maxTagCount={1}
          tokenSeparators={[","]}
          open={dropdownOpen}
          onDropdownVisibleChange={setDropdownOpen}
          onSearch={(value) => {
            // Keep dropdown open only if there's a search value
            setDropdownOpen(!!value);
          }}
          onBlur={() => {
            const value = getReplyToValue();
            if (value && !isValidEmail(value)) {
              setValues({ ...values, replyTo: undefined });
            }
            setDropdownOpen(false);
          }}
        >
          {/* Add a group for sending emails */}
          <Select.OptGroup label="From Selected Sending Emails">
            {selectedEmails.map((emailValue) => {
              const [type, emailId, alias] = emailValue.split(":");
              const email = Object.values(availableEmails)
                .flat()
                .find((e) => e._id === emailId);

              if (type === "alias") {
                return (
                  <Option key={alias} value={alias} label={alias}>
                    <Space>
                      <UserOutlined />
                      <span>{alias.split("@")[0]}</span>
                      <Text type="secondary">&lt;{alias}&gt;</Text>
                      <Badge
                        count="Alias"
                        style={{ backgroundColor: "#e6f4ff", color: "#1890ff" }}
                      />
                    </Space>
                  </Option>
                );
              }

              return email ? (
                <Option
                  key={email.email}
                  value={email.email}
                  label={formatEmailDisplay(email)}
                >
                  <Space>
                    <MailOutlined />
                    <span>{email.name || email.email.split("@")[0]}</span>
                    <Text type="secondary">&lt;{email.email}&gt;</Text>
                  </Space>
                </Option>
              ) : null;
            })}
          </Select.OptGroup>

          {/* Add a group for all available emails */}
          <Select.OptGroup label="All Available Emails">
            {Object.entries(availableEmails).map(([domain, emails]) =>
              emails.map((email) => (
                <Option
                  key={email.email}
                  value={email.email}
                  label={formatEmailDisplay(email)}
                >
                  <Space>
                    <MailOutlined />
                    <span>{email.name || email.email.split("@")[0]}</span>
                    <Text type="secondary">&lt;{email.email}&gt;</Text>
                  </Space>
                </Option>
              ))
            )}
          </Select.OptGroup>
        </Select>
        {values.replyTo && !isValidEmail(getReplyToValue()) && (
          <Text type="danger">Please enter a valid email address</Text>
        )}
      </Form.Item>
    );
  };

  const handleDeleteVariable = (key) => {
    Modal.confirm({
      title: "Delete Variable",
      content: `Are you sure you want to delete the variable "${key}"? This may affect any templates using this variable.`,
      okText: "Delete",
      okType: "danger",
      onOk: () => {
        setValues((prev) => ({
          ...prev,
          variables: prev.variables.filter((v) => v.key !== key),
        }));
      },
    });
  };

  // Add this section to your form, after the HTML Template selection
  const variableSection = (
    <Form.Item
      label="Template Variables"
      tooltip={{
        title:
          "Define variables that can be used in your email template using {{variable_key}} syntax",
        icon: <InfoCircleOutlined />,
      }}
    >
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Button
            type="dashed"
            onClick={handleAddVariable}
            icon={<PlusCircleOutlined />}
            block
          >
            Add Template Variable
          </Button>
        </div>

        {values.variables.length > 0 ? (
          <Table
            dataSource={values.variables}
            rowKey="key"
            pagination={false}
            size="small"
            columns={[
              {
                title: "Variable Key",
                dataIndex: "key",
                key: "key",
                render: (text) => (
                  <Text copyable={{ text: `{{${text}}}` }}>
                    {`{{${text}}}`}
                  </Text>
                ),
              },
              {
                title: "Default Value",
                dataIndex: "defaultValue",
                key: "defaultValue",
              },
              {
                title: "Description",
                dataIndex: "description",
                key: "description",
                ellipsis: true,
              },
              {
                title: "Actions",
                key: "actions",
                width: 120,
                render: (_, record) => (
                  <Space>
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      onClick={() => handleEditVariable(record)}
                    />
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteVariable(record.key)}
                    />
                  </Space>
                ),
              },
            ]}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Space direction="vertical" align="center">
                <Text type="secondary">No template variables defined</Text>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  Add variables to personalize your email template
                </Text>
              </Space>
            }
          />
        )}

        <div style={{ marginTop: 16 }}>
          <Alert
            type="warning"
            showIcon
            message="Using Template Variables"
            description={
              <div>
                <p>
                  Use variables in your template by surrounding the key with
                  double curly braces:
                  <br />
                  <code>{`Hello {{first_name}}!`}</code>
                </p>
                <p>
                  If no specific value is provided, the default value will be
                  used.
                </p>
              </div>
            }
          />
        </div>
      </Card>

      <VariableForm
        variable={editingVariable}
        onSave={handleSaveVariable}
        onCancel={() => setVariableModalVisible(false)}
      />
    </Form.Item>
  );

  return (
    <Row>
      <Col span={24}>
        <Form labelCol={{ span: 4 }} wrapperCol={{ span: 18 }}>
          {/* Start from Template Banner */}
          <Form.Item wrapperCol={{ span: 18, offset: 4 }}>
            <div
              style={{
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderLeft: "4px solid #6366f1",
                borderRadius: 8,
                padding: "16px 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    background: "#eef2ff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ThunderboltOutlined style={{ fontSize: 20, color: "#6366f1" }} />
                </div>
                <div>
                  <Typography.Text strong style={{ fontSize: 14, display: "block", color: "#1e293b" }}>
                    Start with a template
                  </Typography.Text>
                  <Typography.Text style={{ fontSize: 13, color: "#64748b" }}>
                    42 cold outreach templates from HubSpot, Outreach, Apollo & more
                  </Typography.Text>
                </div>
              </div>
              <Button
                type="primary"
                onClick={() => setTemplateGalleryVisible(true)}
                style={{
                  borderRadius: 6,
                  background: "#6366f1",
                  borderColor: "#6366f1",
                }}
              >
                Browse Templates
              </Button>
            </div>
          </Form.Item>

          {/* Campaign Type Selection */}
          <Form.Item label="Campaign Type" required>
            <Select
              name="campaignType"
              placeholder="Select campaign type"
              onChange={handleCampaignTypeChange}
              value={values.campaignType}
            >
              <Option value="all">All Users</Option>
              <Option value="planBased">Plan-Based</Option>
              <Option value="targeted">Targeted Users</Option>
              <Option value="external">External Recipients</Option>
              <Option value="dateRange">Date Range Based</Option>
            </Select>
          </Form.Item>

          {values.campaignType === "dateRange" && (
            <Form.Item
              label="Date Range"
              required
              tooltip={{
                title:
                  "Select date range to target users based on their join date",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Card>
                <Space
                  direction="vertical"
                  style={{ width: "100%" }}
                  size="large"
                >
                  <RangePicker
                    style={{ width: "100%" }}
                    onChange={handleDateRangeChange}
                    value={
                      values.dateRangeFilter.startDate &&
                      values.dateRangeFilter.endDate
                        ? [
                            moment(values.dateRangeFilter.startDate),
                            moment(values.dateRangeFilter.endDate),
                          ]
                        : null
                    }
                    showTime
                  />

                  {fetchingUserCount ? (
                    <Alert message="Fetching users..." type="info" showIcon />
                  ) : (
                    dateRangeUsers &&
                    dateRangeUsers?.length > 0 && (
                      <>
                        <Alert
                          message={`${dateRangeUsers?.length.toLocaleString()} users found in selected date range`}
                          type="success"
                          showIcon
                        />
                        <Card
                          size="small"
                          title="Users in Selected Date Range"
                          extra={
                            <Button
                              type="link"
                              onClick={() => {
                                const csvContent = Papa.unparse(
                                  dateRangeUsers.map((user) => ({
                                    Name: user.name,
                                    Email: user.email,
                                    "Join Date": moment(user.createdAt).format(
                                      "YYYY-MM-DD HH:mm:ss"
                                    ),
                                  }))
                                );
                                const blob = new Blob([csvContent], {
                                  type: "text/csv;charset=utf-8;",
                                });
                                const link = document.createElement("a");
                                link.href = URL.createObjectURL(blob);
                                link.download = `users_${moment().format(
                                  "YYYY-MM-DD"
                                )}.csv`;
                                link.click();
                              }}
                            >
                              Export to CSV
                            </Button>
                          }
                        >
                          <Table
                            columns={dateRangeColumns}
                            dataSource={dateRangeUsers}
                            rowKey="_id"
                            size="small"
                            pagination={{
                              pageSize: 5,
                              showSizeChanger: true,
                              showTotal: (total, range) =>
                                `${range[0]}-${range[1]} of ${total} users`,
                            }}
                            scroll={{ y: 240 }}
                          />
                        </Card>
                      </>
                    )
                  )}
                </Space>
              </Card>
            </Form.Item>
          )}

          {/* Campaign Name */}
          <Form.Item label="Campaign Name" required>
            <Input
              name="name"
              placeholder="Enter campaign name"
              onChange={handleFormChange}
              value={values.name}
            />
          </Form.Item>

          <Form.Item
            label="From Name"
            required
            tooltip={{
              title: "The name that will appear as the sender of the email",
              icon: <InfoCircleOutlined />,
            }}
          >
            <Input
              name="fromName"
              placeholder="Enter sender name"
              onChange={handleFormChange}
              value={values.fromName}
            />
          </Form.Item>

          {/* Email Subject */}
          <Form.Item label="Subject" required>
            <Input
              name="subject"
              placeholder="Enter email subject"
              onChange={handleFormChange}
              value={values.subject}
            />
          </Form.Item>

          {/* HTML Template Selection */}
          <Form.Item
            label={
              <Space>
                <span>HTML Template</span>
                {htmlTemplateMatchInfo && values.htmlTemplate === autoSelectedHtmlTemplate?._id && (
                  <Tag
                    icon={<LinkOutlined />}
                    color="blue"
                    style={{ marginLeft: 8, fontSize: 11 }}
                  >
                    Auto-linked from campaign template
                  </Tag>
                )}
              </Space>
            }
            required
          >
            <Space.Compact style={{ width: "100%" }}>
              <Select
                name="htmlTemplate"
                placeholder="Select an HTML template"
                onChange={handleHtmlTemplateChange}
                value={values.htmlTemplate}
                showSearch
                optionFilterProp="children"
                style={{ flex: 1 }}
                filterOption={(input, option) =>
                  option?.children?.props?.children?.[1]?.toLowerCase().includes(input.toLowerCase()) ||
                  option?.children?.toLowerCase?.()?.includes(input.toLowerCase())
                }
              >
                {templates.map((template) => {
                  const isAutoSelected = autoSelectedHtmlTemplate?._id === template._id;
                  const isRecommended = isAutoSelected && htmlTemplateMatchInfo;

                  return (
                    <Option key={template._id} value={template._id}>
                      <Space style={{ width: "100%", justifyContent: "space-between" }}>
                        <span>{template.name}</span>
                        {isRecommended && (
                          <Tag
                            icon={<StarFilled style={{ fontSize: 10 }} />}
                            color="gold"
                            style={{ marginLeft: 8, fontSize: 10 }}
                          >
                            Recommended
                          </Tag>
                        )}
                      </Space>
                    </Option>
                  );
                })}
              </Select>
              <Tooltip title="Preview template">
                <Button
                  icon={<EyeOutlined />}
                  onClick={handleTemplatePreview}
                  disabled={!values.htmlTemplate}
                >
                  Preview
                </Button>
              </Tooltip>
            </Space.Compact>
            {htmlTemplateMatchInfo && values.htmlTemplate === autoSelectedHtmlTemplate?._id && (
              <div style={{ marginTop: 8 }}>
                <Alert
                  message={
                    <Space>
                      <CheckCircleOutlined style={{ color: "#52c41a" }} />
                      <Text style={{ fontSize: 12 }}>
                        {htmlTemplateMatchInfo.matchReason}
                        <Text type="secondary" style={{ marginLeft: 4 }}>
                          (from "{htmlTemplateMatchInfo.campaignTemplate}")
                        </Text>
                      </Text>
                    </Space>
                  }
                  type="success"
                  style={{
                    padding: "6px 12px",
                    background: "#f6ffed",
                    border: "1px solid #b7eb8f",
                  }}
                />
              </div>
            )}
          </Form.Item>

          {/* Variables */}
          {variableSection}

          {/* Conditional rendering based on campaign type */}
          {values.campaignType === "planBased" && (
            <Form.Item label="Included Plans">
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder="Select plans to include"
                onChange={(selectedPlans) =>
                  setValues({ ...values, includedPlans: selectedPlans })
                }
                value={values.includedPlans}
              >
                {plans?.map((plan) => (
                  <Option key={plan._id} value={plan._id}>
                    {plan.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {values.campaignType === "targeted" && (
            <Form.Item label="Target Users">
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder="Search and select users to target"
                value={values.targetedUsers}
                onChange={handleTargetedUsersChange}
                onSearch={handleUserSearch}
                loading={searching}
                filterOption={false}
              >
                {userOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {values.campaignType === "external" &&
            renderExternalRecipientsSection()}

          {values.campaignType !== "targeted" &&
            values.campaignType !== "external" && (
              <Form.Item label="Exclude Users">
                <Select
                  mode="multiple"
                  style={{ width: "100%" }}
                  placeholder="Search and select users to exclude"
                  value={excludedUsers}
                  onChange={handleExcludedUsersChange}
                  onSearch={handleUserSearch}
                  loading={searching}
                  filterOption={false}
                >
                  {userOptions.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

          {/* Scheduled Date */}
          <Form.Item label="Schedule For">
            <Space direction="vertical" style={{ width: "100%" }}>
              {/* DatePicker for date-time selection */}
              <DatePicker
                showTime
                style={{ width: "100%" }}
                onChange={handleDateChange}
                value={
                  values.scheduledFor
                    ? moment.tz(values.scheduledFor, timezone)
                    : null
                }
                format={`YYYY-MM-DD HH:mm:ss [${timezone}]`}
              />

              {/* Timezone selection dropdown */}
              <Select
                value={timezone}
                showSearch
                style={{ width: "100%" }}
                placeholder="Select a timezone"
                onChange={handleTimezoneChange}
                filterOption={(input, option) =>
                  option.children
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {timezones.map((tz) => (
                  <Option key={tz} value={tz}>
                    {formatTimezone(tz)}
                  </Option>
                ))}
              </Select>
            </Space>
          </Form.Item>

          {/* Add this right after the HTML Template Form.Item */}
          <Form.Item
            label="Sending Emails"
            required
            tooltip={{
              title:
                "Select email addresses and aliases to send this campaign from. Using multiple addresses can help improve delivery rates.",
              icon: <InfoCircleOutlined />,
            }}
          >
            <Card>
              <Select
                mode="multiple"
                style={{ width: "100%", marginBottom: 16 }}
                placeholder="Select emails and aliases to send from..."
                value={selectedEmails}
                onChange={handleEmailSelection}
                optionLabelProp="label"
                optionFilterProp="label"
                options={formatEmailsForSelect()}
                listHeight={400}
                maxTagCount={5}
                virtual={true}
                dropdownStyle={{ minWidth: 400 }}
                dropdownRender={(menu) => (
                  <div>
                    {menu}
                    <Divider style={{ margin: "8px 0" }} />
                    <Space style={{ padding: "0 8px 4px" }}>
                      <InfoCircleOutlined />
                      <Text type="secondary">
                        Tip: Using multiple sending addresses can improve
                        delivery rates
                      </Text>
                    </Space>
                  </div>
                )}
              />

              {selectedEmails.length > 0 ? (
                <Card
                  size="small"
                  title="Selected Sending Configuration"
                  bordered={false}
                  style={{ background: "#f5f5f5" }}
                >
                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: "100%" }}
                  >
                    <Space>
                      <Badge
                        count={selectedDetails.totalEmails}
                        style={{ backgroundColor: "#1890ff" }}
                      />
                      <Text>Total sending addresses selected</Text>
                    </Space>
                    <Space>
                      <Badge
                        count={selectedDetails.baseEmails}
                        style={{ backgroundColor: "#52c41a" }}
                      />
                      <Text>Primary email addresses</Text>
                    </Space>
                    <Space>
                      <Badge
                        count={selectedDetails.aliases}
                        style={{ backgroundColor: "#722ed1" }}
                      />
                      <Text>Email aliases</Text>
                    </Space>
                    <Space>
                      <Badge
                        count={selectedDetails.domains.size}
                        style={{ backgroundColor: "#faad14" }}
                      />
                      <Text>Unique sending domains</Text>
                    </Space>
                  </Space>
                </Card>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <Space direction="vertical" align="center">
                      <Text type="secondary">No sending emails selected</Text>
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        Select at least one email address or alias to send from
                      </Text>
                    </Space>
                  }
                />
              )}
            </Card>
          </Form.Item>

          {renderReplyToSection()}

          {/* CC */}
          <Form.Item
            label="CC"
            tooltip={{
              title:
                "Enter email addresses for CC. Separate multiple emails with commas or spaces and press Enter.",
              icon: <InfoCircleOutlined />,
            }}
          >
            <Input
              value={ccInput}
              onChange={handleCcInputChange}
              onPressEnter={handleCcInputPressEnter}
              placeholder="Enter email addresses and press Enter"
            />
            {values.cc.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {values.cc.map((email) => (
                  <Tag
                    key={email}
                    closable
                    onClose={() => handleRemoveCc(email)}
                    style={{ margin: "4px" }}
                  >
                    {email}
                  </Tag>
                ))}
              </div>
            )}
          </Form.Item>

          {/* BCC */}
          <Form.Item
            label="BCC"
            tooltip={{
              title:
                "Enter email addresses for BCC. Separate multiple emails with commas or spaces and press Enter.",
              icon: <InfoCircleOutlined />,
            }}
          >
            <Input
              value={bccInput}
              onChange={handleBccInputChange}
              onPressEnter={handleBccInputPressEnter}
              placeholder="Enter email addresses and press Enter"
            />
            {values.bcc.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {values.bcc.map((email) => (
                  <Tag
                    key={email}
                    closable
                    onClose={() => handleRemoveBcc(email)}
                    style={{ margin: "4px" }}
                  >
                    {email}
                  </Tag>
                ))}
              </div>
            )}
          </Form.Item>

          {/* Email Sending Volume */}
          <Form.Item
            label="Email Sending Volume"
            tooltip="Control the sending volume for this campaign."
          >
            <EmailVolumeControl
              totalRecipients={
                values.campaignType === "external"
                  ? values.externalRecipients?.length
                  : values.campaignType === "dateRange"
                  ? dateRangeUsers?.length
                  : values.totalEmails || 0
              }
              initialMinutesBetweenEmails={
                values.emailSendingConfig?.minutesBetweenEmails || 5
              }
              customInterval={
                values.emailSendingConfig?.customInterval || false
              }
              emailsPerHour={values.emailSendingConfig?.emailsPerHour || 12}
              senderCount={values.emailsToUse?.length || 1}
              initialSendingMode={
                values.emailSendingConfig?.sendingMode || "time_based"
              }
              initialEmailsPerMinute={
                values.emailSendingConfig?.emailsPerMinute || 20
              }
              onVolumeChange={(config) => {
                setValues((prevValues) => ({
                  ...prevValues,
                  emailSendingConfig: {
                    ...prevValues.emailSendingConfig,
                    ...config,
                  },
                }));
              }}
            />
          </Form.Item>

          {/* Submit button */}
          <Form.Item wrapperCol={{ span: 12, offset: 4 }}>
            <Button type="primary" onClick={handleFormSubmit} loading={loading}>
              Create Campaign
            </Button>
          </Form.Item>
        </Form>

        {renderRecipientModal()}

        {/* Campaign Template Gallery */}
        <CampaignTemplateGallery
          visible={templateGalleryVisible}
          onClose={() => setTemplateGalleryVisible(false)}
          onSelect={handleTemplateSelect}
        />

        {/* HTML Template Preview Modal */}
        <Modal
          title={templatePreviewData?.name || "Template Preview"}
          visible={templatePreviewVisible}
          onCancel={() => {
            setTemplatePreviewVisible(false);
            setTemplatePreviewData(null);
          }}
          footer={[
            <Button
              key="close"
              onClick={() => {
                setTemplatePreviewVisible(false);
                setTemplatePreviewData(null);
              }}
            >
              Close
            </Button>,
          ]}
          width={900}
          bodyStyle={{ padding: 0, height: "70vh" }}
          centered
        >
          {templatePreviewLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Spin size="large" tip="Loading preview..." />
            </div>
          ) : templatePreviewData?.compiledHtml ? (
            <iframe
              srcDoc={templatePreviewData.compiledHtml}
              title="Email Preview"
              style={{
                width: "100%",
                height: "100%",
                border: "none",
              }}
            />
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                color: "#999",
              }}
            >
              No preview available. Template may not have compiled HTML.
            </div>
          )}
        </Modal>
      </Col>
    </Row>
  );
};

export default withRouter(EmailCampaignCreateForm);
